import LegalDocShell from "@/components/legal/LegalDocShell";
import { buildLegalSections } from "@/lib/legalRenderer";

// Full 30-section NaturePulse Cookie Policy. Anchor IDs are stable.
const SECTIONS = buildLegalSections([
  { id: "what-are-cookies", title: "1. What Are Cookies?", body:
`Cookies are small text files that websites place on your computer, smartphone, tablet, or other device when you visit a website.

Cookies can help a website remember information about your visit, such as:
- Whether you are signed in
- Your preferred language
- Your privacy preferences
- Your display settings
- Your location preferences
- Features you have used
- Pages you have visited
- Information needed to keep the website secure and functioning properly

Some cookies last only during your browsing session, while others may remain on your device for a longer period.`
  },
  { id: "similar-tech", title: "2. Similar Technologies", body:
`NaturePulse may also use technologies that function similarly to cookies, including:
- Local storage
- Session storage
- Software development kits ("SDKs")
- Pixels
- Tags
- Device identifiers
- Browser identifiers
- Authentication tokens
- Similar technologies

For simplicity, this Policy generally refers to all of these technologies as "cookies."`
  },
  { id: "why-cookies", title: "3. Why NaturePulse Uses Cookies", body:
`NaturePulse may use cookies to:
- Operate the website
- Keep users signed in
- Authenticate accounts
- Protect accounts from unauthorized access
- Remember user preferences
- Remember language selections
- Remember privacy choices
- Remember map and location preferences
- Save display settings
- Provide NaturePulse and Farmers Pulse functionality
- Maintain user sessions
- Prevent fraud, spam, and abuse
- Diagnose technical problems
- Measure website performance
- Understand how visitors use NaturePulse
- Improve website design and navigation
- Improve features and services
- Personalize relevant content
- Support notifications
- Support embedded services
- Meet legal and regulatory requirements`
  },
  { id: "necessary", title: "4. Strictly Necessary Cookies", body:
`These cookies are required for NaturePulse to function properly. They may be used for:
- User login
- Authentication
- Account security
- Session management
- Fraud prevention
- Privacy preference storage
- Load balancing
- Website stability
- Essential form functions

Because these cookies are necessary for the operation and security of the website, they generally cannot be disabled through our cookie-preference tools. You may be able to block them through your browser, but doing so may prevent parts of NaturePulse from functioning properly.`
  },
  { id: "preferences", title: "5. Preference and Functional Cookies", body:
`These cookies help NaturePulse remember choices you make. They may remember:
- Language preferences
- Country or region
- Display preferences
- Map settings
- Measurement units
- Weather preferences
- Farming preferences
- Accessibility settings
- Previously selected filters
- Location privacy preferences
- Other customized settings

For example, Farmers Pulse may remember whether you prefer temperatures displayed in Celsius or Fahrenheit or which geographic area you last viewed.`
  },
  { id: "analytics", title: "6. Analytics and Performance Cookies", body:
`NaturePulse may use analytics cookies to understand how visitors interact with the website. These cookies may help us understand:
- Number of visitors
- Pages viewed
- Most commonly used features
- Search activity
- Navigation patterns
- Approximate visitor region
- Device type
- Browser type
- Page performance
- Errors and crashes
- Time spent on pages
- General usage patterns

We use this information to improve NaturePulse and Farmers Pulse. Where appropriate, we may configure analytics tools to reduce or limit the collection of identifiable information.`
  },
  { id: "security", title: "7. Security Cookies", body:
`NaturePulse may use cookies and related technologies to protect users and the platform. These technologies may help:
- Detect suspicious login activity
- Detect automated attacks
- Prevent spam
- Prevent abuse
- Identify fraudulent activity
- Protect user accounts
- Protect forms
- Limit unauthorized requests
- Maintain system integrity

Security cookies may be considered strictly necessary.`
  },
  { id: "location", title: "8. Location-Related Cookies", body:
`NaturePulse and Farmers Pulse contain location-aware features. Cookies may be used to remember:
- Country
- State or province
- District or county
- City or general region
- Preferred map location
- Recently viewed locations
- Farm region
- Weather location
- Location privacy settings

Cookies themselves do not necessarily provide precise GPS information. If NaturePulse requests access to your device's precise location, your browser or device may separately request your permission. You can generally control device location permissions through your browser, operating system, or mobile device settings.`
  },
  { id: "farmers-pulse", title: "9. Farmers Pulse Cookies", body:
`Farmers Pulse may use cookies to improve agricultural features and remember preferences such as:
- Selected farm
- Selected field
- Preferred crops
- Agricultural region
- Planting calendar settings
- Weather location
- Recently viewed crop information
- Market filters
- Pest and disease filters
- Measurement units
- Notification preferences
- Map preferences
- Public or private location settings

These cookies help provide a more relevant farming experience without requiring you to repeatedly enter the same preferences.`
  },
  { id: "authentication", title: "10. Authentication Cookies", body:
`If you create or sign in to a NaturePulse account, authentication cookies may be used to recognize your session. These cookies help prevent you from having to log in again on every page.

Authentication cookies may also be used to:
- Verify your identity
- Protect account access
- Maintain secure sessions
- Support multi-step authentication
- Detect unusual account activity

You should avoid using the "remember me" or persistent login function on shared or public computers.`
  },
  { id: "embedded", title: "11. Embedded Content and Third-Party Services", body:
`NaturePulse may display or integrate content and services provided by third parties. Examples may include:
- Maps
- Weather services
- Videos
- Social media posts
- News links
- Research resources
- Analytics services
- AI services
- Payment services
- Authentication providers
- Advertising services, if introduced in the future

Third-party services may place or access cookies according to their own policies. NaturePulse does not control cookies placed directly by independent third parties. We encourage users to review the privacy and cookie policies of those providers.`
  },
  { id: "maps", title: "12. Maps", body:
`NaturePulse may use third-party map providers to display geographic observations. Map providers may use cookies or similar technologies when maps are loaded or used. These technologies may support:
- Map rendering
- Location search
- Map preferences
- Performance
- Security
- Usage statistics

The third-party map provider's own privacy and cookie practices may apply.`
  },
  { id: "weather", title: "13. Weather Services", body:
`NaturePulse and Farmers Pulse may use third-party weather providers. Weather services may receive information such as:
- Requested location
- Approximate geographic area
- IP address
- Device or browser information

Third-party weather services may use cookies according to their own privacy policies.`
  },
  { id: "ai-services", title: "14. Artificial Intelligence Services", body:
`NaturePulse may integrate third-party AI or machine-learning providers. AI services themselves may not always require browser cookies, but some AI-enabled features or supporting infrastructure may use cookies or session technologies for:
- Authentication
- Security
- Request management
- Rate limiting
- Feature preferences
- Session continuity

For more information about AI-related processing, please review the [NaturePulse AI Policy](/legal/ai) and Privacy Policy.`
  },
  { id: "social", title: "15. Social Media and Sharing Features", body:
`NaturePulse may allow users to share content to social networks or display embedded social-media content. Third-party platforms may place cookies when you interact with:
- Share buttons
- Embedded posts
- Embedded videos
- Social media widgets
- Login services provided by social platforms

Those cookies are controlled by the relevant third party.`
  },
  { id: "advertising", title: "16. Advertising Cookies", body:
`NaturePulse may or may not use advertising services depending on the features offered at a particular time.

If advertising technologies are introduced, advertising cookies may be used to:
- Measure advertising performance
- Limit repeated advertising
- Understand general interests
- Deliver relevant advertising
- Measure conversions

Where required by applicable law, NaturePulse will request appropriate consent before activating non-essential advertising cookies. We will update this Cookie Policy if our advertising practices materially change.`
  },
  { id: "affiliate", title: "17. Affiliate Links", body:
`NaturePulse may occasionally include links to third-party products, services, agricultural resources, travel services, equipment, books, or other external resources. Some links may be affiliate links.

If you follow an affiliate link, the destination website may place cookies to identify that you were referred by NaturePulse. Affiliate tracking is generally controlled by the third-party provider. Where appropriate, NaturePulse will disclose affiliate relationships.`
  },
  { id: "first-third-party", title: "18. First-Party and Third-Party Cookies", body:
`Cookies may be categorized as:

## First-Party Cookies

Placed directly by NaturePulse.net or services operating on our behalf. They may support:
- Login
- Preferences
- Security
- Website functionality
- Analytics

## Third-Party Cookies

Placed by other companies whose services are integrated into NaturePulse. Examples:
- Analytics providers
- Map providers
- Video providers
- Social networks
- Advertising providers
- Authentication providers`
  },
  { id: "session-persistent", title: "19. Session and Persistent Cookies", body:
`NaturePulse may use both session cookies and persistent cookies.

## Session Cookies
Generally disappear when you close your browser. They may help maintain temporary information while you navigate the site.

## Persistent Cookies
Remain on your device after your browser closes until they expire, you delete them, or they are replaced. Persistent cookies may be used to remember preferences or authentication choices.`
  },
  { id: "duration", title: "20. Cookie Duration", body:
`The length of time a cookie remains on your device depends on its purpose. NaturePulse will seek to avoid retaining cookies longer than reasonably necessary for their intended purpose.

Specific retention periods may vary based on:
- Cookie type
- Service provider
- Security requirements
- User preferences
- Legal requirements`
  },
  { id: "consent", title: "21. Cookie Consent", body:
`Where required by applicable law, NaturePulse may display a cookie-consent banner or preference center. The banner may provide options such as:
- Accept All
- Reject Non-Essential Cookies
- Manage Preferences

Users should be able to make a meaningful choice regarding optional cookies. Strictly necessary cookies may remain active because they are required to operate the website.

For details on EU/EEA/UK-specific consent handling, see the [EU, EEA & UK Cookie Consent Supplement](/legal/cookies-eu).`
  },
  { id: "categories", title: "22. Recommended Cookie Preference Categories", body:
`NaturePulse may provide a Cookie Settings panel containing categories such as:
- Necessary — Always Active
- Preferences
- Analytics
- Advertising
- Third-Party Media

Users can enable or disable optional categories where available.`
  },
  { id: "change-prefs", title: "23. Changing Your Cookie Preferences", body:
`You may change your preferences at any time through the Cookie Settings link available on NaturePulse.net.

Changing your preferences will generally apply to future cookie use. Previously stored cookies may need to be deleted through your browser if you want them immediately removed.`
  },
  { id: "browser-controls", title: "24. Browser Cookie Controls", body:
`Most browsers allow users to:
- View cookies
- Delete cookies
- Block cookies
- Block third-party cookies
- Clear browsing data
- Restrict tracking

These settings are usually available through your browser's privacy or security menu. Blocking cookies may affect certain NaturePulse features. For example, you may need to log in repeatedly or some preferences may not be remembered.`
  },
  { id: "gpc", title: "25. Global Privacy Controls and Similar Signals", body:
`Certain browsers and extensions allow users to send privacy preference signals, including Global Privacy Control (GPC) or similar mechanisms.

Where applicable law requires us to recognize such signals, NaturePulse will seek to honor them appropriately. The availability and legal effect of these mechanisms may vary by jurisdiction.`
  },
  { id: "dnt", title: "26. Do Not Track", body:
`Some browsers provide a "Do Not Track" setting. There is not currently a universally adopted technical standard governing all Do Not Track signals.

NaturePulse may respond to legally recognized privacy preference mechanisms where required by applicable law.`
  },
  { id: "mobile", title: "27. Mobile Devices", body:
`If NaturePulse services are accessed through a mobile device or future mobile application, device settings may provide additional privacy controls. These may include:
- Location permission
- Camera permission
- Photo-library permission
- Notification permission
- Tracking preferences

You can generally modify these permissions through your device settings.`
  },
  { id: "children", title: "28. Children's Privacy", body:
`NaturePulse does not intend to use cookies to knowingly create behavioral advertising profiles of children in violation of applicable law.

If NaturePulse introduces features specifically intended for children or schools, additional protections and cookie restrictions may apply. Please review our [Youth Privacy Policy](/about/youth-privacy) for additional information about children's privacy.`
  },
  { id: "changes", title: "29. Changes to This Cookie Policy", body:
`NaturePulse may update this Cookie Policy as:
- Technology changes
- New features are introduced
- Service providers change
- Legal requirements change
- Our cookie practices change

When significant changes occur, we may provide notice through NaturePulse.net, the cookie banner, account notifications, email, or another reasonable method. The Last Updated date above indicates when this Policy was most recently revised.`
  },
  { id: "contact", title: "30. Contact Us", body:
`Questions regarding cookies or privacy practices may be directed to:

NaturePulse.net, a division of Social Pulse Inc.
Website: [NaturePulse.net](https://naturepulse.net)
Privacy Email: legal@naturepulse.net
Support Email: support@naturepulse.net

Mailing Address:
Social Pulse Inc.
15166 SE Aston Loop
Portland, OR 97236
United States`
  },
]);

export default function CookiePolicy() {
  return (
    <LegalDocShell
      eyebrow="NaturePulse · Cookie Policy"
      title="Cookie Policy"
      effective="August 23, 2026"
      updated="August 23, 2026"
      intro={
        <>
          <p>NaturePulse.net, a division of Social Pulse Inc. ("NaturePulse," "we," "us," or "our") uses cookies and similar technologies to help operate, secure, improve, and personalize NaturePulse.net and related services, including Farmers Pulse.</p>
          <p>This Cookie Policy explains what cookies are, how we use them, the types of cookies and similar technologies that may be used, and the choices available to you.</p>
          <p>This Cookie Policy should be read together with our <a href="/privacy">Privacy Policy</a> and <a href="/legal/terms">Terms of Service</a>.</p>
        </>
      }
      sections={SECTIONS}
    />
  );
}
